import React from 'react';

/**
 * Server-side guess at the visitor's device, so SSR renders the layout they will
 * actually see instead of always rendering mobile. The browser reads the same value
 * back from `#root` for its hydration render, so a wrong guess only costs one
 * post-hydration re-render (the behaviour before this existed), never a mismatch.
 */
export type DeviceClass = 'mobile' | 'desktop';

export const DEVICE_CLASS_ATTR = 'data-device';

/** Defaults to mobile: the pre-existing SSR behaviour, and what tests render with. */
export const DeviceClassContext = React.createContext<DeviceClass>('mobile');

// Tablets are deliberately absent: they report as desktop (iPadOS) or omit "Mobi" (Android).
const MOBILE_UA = /Mobi|iPhone|iPod|Opera Mini|IEMobile|BlackBerry/i;

type Headers = Record<string, string | string[] | undefined>;

function header(headers: Headers, name: string): string | undefined {
    const value = headers[name];
    return Array.isArray(value) ? value[0] : value;
}

export function detectDeviceClass(headers: Headers = {}): DeviceClass {
    // Low-entropy client hint, sent by default by Chromium browsers over HTTPS.
    const hint = header(headers, 'sec-ch-ua-mobile');
    if (hint === '?1') return 'mobile';
    if (hint === '?0') return 'desktop';

    return MOBILE_UA.test(header(headers, 'user-agent') || '') ? 'mobile' : 'desktop';
}

export function parseDeviceClass(value: unknown): DeviceClass {
    return value === 'desktop' ? 'desktop' : 'mobile';
}
